/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { basketOperations, basketTypes } from '../../store/basket';
import { goToRoute, withParams } from '../../lib/router';
import {
  removeAccommodationFromBasket,
  removeExtraFromBasket,
  removeNameChangeFromBasket,
  removePaxFromBasket,
  removeTicketFromBasket,
  removeTransportFromBasket,
} from '../../api/basket';

import { DAS_REMOVE_ITEM_FROM_BASKET_FAILED } from '../../constants/errors';
import constants from '../../constants';
import { createError } from '../../store/errors';
import { messageOperations } from '../../store/messages';
import { reloadBasket } from './helpers';

const { REMOVE_ITEMS } = basketTypes;
const { sendMessage } = messageOperations;

const isBasketEmpty = basket => {
  const accommodations = basket?.accommodations ?? [];
  const extras = basket?.extras ?? [];
  const tickets = basket?.tickets ?? [];
  const transport = basket?.transport ?? [];
  const nameChanges = basket?.charges?.CHANGE_TICKET_CUSTOMER_DATA ?? [];

  return !(
    accommodations.length ||
    extras.length ||
    tickets.length ||
    transport.length ||
    nameChanges.length
  );
};

const removeItems = function* removeItems(bookingRef, items, promise) {
  const success = true;

  if (!items || items.length < 1) {
    return success;
  }

  let index = 0;
  while (index < items.length) {
    const removeFromBasketResponse = yield call(
      promise,
      bookingRef,
      items[index],
    );

    if (removeFromBasketResponse.errors) {
      return removeFromBasketResponse;
    }
    index += 1;
  }

  return success;
};

export const performRemoveItemsFromBasket = function* performRemoveItemsFromBasket({
  payload,
}) {
  try {
    const { items, bookingRef } = payload;

    const {
      accommodations,
      extras,
      tickets,
      transport,
      nameChanges,
      pax,
    } = items;

    const accommodationsResponse = yield* removeItems(
      bookingRef,
      accommodations,
      removeAccommodationFromBasket,
    );

    if (accommodationsResponse.errors) {
      // Handle web service errors.
      yield* reloadBasket(bookingRef);
      yield all(
        accommodationsResponse.errors.map(error => put(createError(error))),
      );
      return;
    }

    const extrasResponse = yield* removeItems(
      bookingRef,
      extras,
      removeExtraFromBasket,
    );

    if (extrasResponse.errors) {
      // Handle web service errors.
      yield* reloadBasket(bookingRef);
      yield all(extrasResponse.errors.map(error => put(createError(error))));
      return;
    }

    const ticketsResponse = yield* removeItems(
      bookingRef,
      tickets,
      removeTicketFromBasket,
    );

    if (ticketsResponse.errors) {
      // Handle web service errors.
      yield* reloadBasket(bookingRef);
      yield all(ticketsResponse.errors.map(error => put(createError(error))));
      return;
    }

    const transportResponse = yield* removeItems(
      bookingRef,
      transport,
      removeTransportFromBasket,
    );

    if (transportResponse.errors) {
      // Handle web service errors.
      yield* reloadBasket(bookingRef);
      yield all(transportResponse.errors.map(error => put(createError(error))));
      return;
    }

    const nameChangesResponse = yield* removeItems(
      bookingRef,
      nameChanges,
      removeNameChangeFromBasket,
    );

    if (nameChangesResponse.errors) {
      // Handle web service errors.
      yield* reloadBasket(bookingRef);
      yield all(
        nameChangesResponse.errors.map(error => put(createError(error))),
      );
      return;
    }

    const paxResponse = yield* removeItems(
      bookingRef,
      pax,
      removePaxFromBasket,
    );

    if (paxResponse.errors) {
      // Handle web service errors.
      yield* reloadBasket(bookingRef);
      yield all(paxResponse.errors.map(error => put(createError(error))));
      return;
    }

    const basketResponse = yield* reloadBasket(bookingRef);

    if (basketResponse.errors) {
      return;
    }

    if (isBasketEmpty(basketResponse.basket)) {
      yield all([
        put(basketOperations.resetBasket(bookingRef)),
        put(
          goToRoute(
            withParams(constants.BOOKING_URL, {
              booking_reference: bookingRef,
            }),
          ),
        ),
      ]);
    }

    yield put(
      sendMessage({
        type: 'success',
        displayType: 'toast',
        message: `Items successfully removed from your basket.`,
      }),
    );
  } catch (error) {
    yield put(
      createError({
        code: DAS_REMOVE_ITEM_FROM_BASKET_FAILED,
      }),
    );
  }
};

export default takeLatest(REMOVE_ITEMS, performRemoveItemsFromBasket);
